import clsx from 'clsx';
import { forwardRef, memo, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSet } from 'react-use';
import { SwiperButton } from 'src/components/Buttons/SwiperButton';
import { useTrackComponent } from 'src/contexts/AppPerformanceContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import {
  InventoryEventDetailContextProvider,
  useInventoryEventDetailContext,
} from 'src/contexts/InventoryEventDetailContext/InventoryEventDetailContext';
import { Stack } from 'src/core/ui';
import { mergeRefs } from 'src/core/utils';
import { useListingGroupPricingSettings } from 'src/hooks/api/useListingGroupPricingSettings';
import { useGetListingAutoPricingSettings } from 'src/hooks/useGetListingAutoPricingSettings';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  Event,
  Feature,
  Listing,
  ListingDetailsAutoPricingSectionUpdates,
  ListingGroup,
  ListingGroupAutoPriceSettings,
} from 'src/WebApiController';

import { SidePanelTab } from '../MarketListings/MarketListingHeader';
import { DetailPanelBody } from './components/DetailPanel/DetailPanelBody';
import { MapBody } from './components/Map/MapBody';
import { MapFooter } from './components/Map/MapFooter';
import { MapHeader } from './components/Map/MapHeader';
import * as styles from './InventoryEventDetail.css';
import {
  getMergedSelectedSectionSettings,
  useListingScoreRecommendations,
  useOnePagerContainerViewMode,
} from './InventoryEventDetail.hooks';

const InventoryEventDetailRaw = ({
  event,
  eventListings,
  selectedListingIds,
}: {
  event: Event;
  eventListings?: Listing[] | null;
  selectedListingIds: number[];
}) => {
  useTrackComponent('InventoryEventDetail');

  const hasSelectedSectionScoreRecommendationsFeature = useUserHasFeature(
    Feature.SelectedSectionScoreRecommendations
  );
  const hasVeloCBFeature = useUserHasFeature(
    Feature.AutoPricingImprovedPricePreivewForVeloCB
  );
  const { venueMapInfo } = useEventMapContext();
  const {
    useVerticalView,
    setForceToVerticalView,
    onePagerContainerRef,
    onOnePagerContainerRefInitialized,
  } = useOnePagerContainerViewMode();

  const [listingRecommendationDeclined, listingRecommendationDeclinedMutation] =
    useSet<number>();

  const selectedListing = useMemo(() => {
    if (selectedListingIds.length <= 1) {
      let listing = eventListings?.find((l) => l.id === selectedListingIds[0]);
      if (!listing) {
        listing = eventListings
          ?.flatMap((l) => flattenListingGroup(l))
          .find((l) => l.isFull && l.id === selectedListingIds[0]) as Listing;
      }

      if (!listing?.isLtGrp) {
        return listing;
      }
    }
    return undefined;
  }, [eventListings, selectedListingIds]);

  const selectedGroup = useMemo(() => {
    return eventListings?.find((l) => {
      const lg = l as ListingGroup;

      return (
        lg.isLtGrp &&
        lg.groupType == 'Leader' &&
        selectedListingIds.includes(lg.id)
      );
    }) as ListingGroup | undefined;
  }, [eventListings, selectedListingIds]);

  const leadingListingInGroup = useMemo(() => {
    if (!selectedGroup) {
      return null;
    }

    return flattenListingGroup(selectedGroup)
      .toSorted((a, b) => (a.ltGrpPrior ?? 0) - (b.ltGrpPrior ?? 0))
      .at(0) as Listing;
  }, [selectedGroup]);

  const isRecommendationDeclined = useMemo(() => {
    return Boolean(
      selectedListing?.id &&
      listingRecommendationDeclined.has(selectedListing.id)
    );
  }, [listingRecommendationDeclined, selectedListing?.id]);

  const {
    pricingSettings: listingPricingSettings,
    loaded: listingSettingLoaded,
  } = useGetListingAutoPricingSettings(selectedListing, event);

  const { sectionRowScoreRecommendations } = useListingScoreRecommendations(
    event,
    venueMapInfo,
    selectedListing,
    !hasSelectedSectionScoreRecommendationsFeature
  );

  // Recommendation only applies to settings with no section filters
  const sanitizedRecommendations = useMemo(() => {
    if (
      listingPricingSettings?.compListingSelectedSectionSettings
        ?.sectionIdFilter &&
      listingPricingSettings?.compListingSelectedSectionSettings
        ?.sectionIdFilter.length > 0
    ) {
      return undefined;
    }
    return sectionRowScoreRecommendations;
  }, [
    listingPricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter,
    sectionRowScoreRecommendations,
  ]);

  const { pricingSettings: groupPricingSettings, isPricingSettingsLoading } =
    useListingGroupPricingSettings(selectedGroup?.ltGrpId, event);

  const listingFormMethods = useForm<ListingDetailsAutoPricingSectionUpdates>({
    defaultValues: {},
  });

  const startingPrice = leadingListingInGroup?.listPrice ?? null;

  const listingGroupFormMethods = useForm<ListingGroupAutoPriceSettings>({
    defaultValues: {
      desiredActiveListings: null,
      deprioritizedQuantities: null,
      undAbsAmt: null,
      undRelAmt: null,
      netProceedsFloor: null,
      netProceedsCeiling: null,
      autoPricingEnabled: null,
      startingUnitNetProceeds: startingPrice,
    },
  });

  useEffect(() => {
    if (listingSettingLoaded && selectedListing?.id && listingPricingSettings) {
      if (selectedListing?.id !== listingPricingSettings.id) {
        // Only reset the form when listing pricing settings is for the exact selected listing
        // This is to avoid the race condition where switching selectedListing triggered different
        // GetListingAutoPricingSettings that can result in the wrong listing settings being assigned
        return;
      }
      if (
        hasSelectedSectionScoreRecommendationsFeature &&
        !isRecommendationDeclined
      ) {
        const selectedSectionSettingsWithRecommendations =
          getMergedSelectedSectionSettings(
            listingPricingSettings?.compListingSelectedSectionSettings,
            sanitizedRecommendations?.recommendedSelectedSectionSettings
          );
        const pricingSettingsWithScoreRecommendations = {
          ...listingPricingSettings,
          compListingSelectedSectionSettings:
            selectedSectionSettingsWithRecommendations,
        };
        const resetData = {
          ...(pricingSettingsWithScoreRecommendations ?? {}),
        };

        if (hasVeloCBFeature) {
          resetData.velocityCircuitBreakerStateRowVersion =
            selectedListing.cirBrSuppRowVer ?? null;
        }
        listingFormMethods.reset(resetData);
      } else {
        const resetData = {
          ...(listingPricingSettings ?? {}),
        };

        if (hasVeloCBFeature) {
          resetData.velocityCircuitBreakerStateRowVersion =
            selectedListing.cirBrSuppRowVer;
        }
        listingFormMethods.reset(resetData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listingPricingSettings,
    listingSettingLoaded,
    selectedListing?.id,
    selectedListing?.cirBrSuppRowVer,
    sanitizedRecommendations?.recommendedSelectedSectionSettings,
  ]);

  useEffect(() => {
    if (!isPricingSettingsLoading && selectedGroup) {
      const { listingGroupId, ...rest } = groupPricingSettings ?? {};
      if (listingGroupId !== selectedGroup.ltGrpId) {
        // Same reason, we want to only reset the pricing setting for the selected listing group
        return;
      }

      listingGroupFormMethods.reset({
        ...rest,
        deprioritizedQuantities: selectedGroup.deprioritizedQuantities,
        desiredActiveListings: selectedGroup.desiredActiveListings,
        undAbsAmt: selectedGroup.absoluteRankPremium,
        undRelAmt: selectedGroup.relativeRankPremium,
        autoPricingEnabled: selectedGroup.isAutoPrc,
        netProceedsFloor: selectedGroup.procsFloor,
        netProceedsCeiling: selectedGroup.procsCeil,
        startingUnitNetProceeds: startingPrice,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, isPricingSettingsLoading, groupPricingSettings]);

  return selectedGroup ? (
    <FormProvider {...listingGroupFormMethods}>
      <InventoryEventDetailContextProvider<ListingGroupAutoPriceSettings>
        event={event}
        eventListings={eventListings}
        selectedListingIds={selectedListingIds}
        selectedListing={selectedListing}
        selectedGroup={selectedGroup}
      >
        <InventoryEventDetailContent
          useVerticalView={useVerticalView}
          setForceToVerticalView={setForceToVerticalView}
          onRefInitialized={onOnePagerContainerRefInitialized}
          ref={onePagerContainerRef}
        />
      </InventoryEventDetailContextProvider>
    </FormProvider>
  ) : (
    <FormProvider {...listingFormMethods}>
      <InventoryEventDetailContextProvider<ListingDetailsAutoPricingSectionUpdates>
        event={event}
        eventListings={eventListings}
        selectedListingIds={selectedListingIds}
        selectedListing={selectedListing}
        selectedGroup={selectedGroup}
        sectionRowScoreRecommendations={sanitizedRecommendations}
        isRecommendationDeclined={isRecommendationDeclined}
        listingRecommendationDeclinedMutation={
          listingRecommendationDeclinedMutation
        }
      >
        <InventoryEventDetailContent
          useVerticalView={useVerticalView}
          setForceToVerticalView={setForceToVerticalView}
          onRefInitialized={onOnePagerContainerRefInitialized}
          ref={onePagerContainerRef}
        />
      </InventoryEventDetailContextProvider>
    </FormProvider>
  );
};

// eslint-disable-next-line react/display-name
export const InventoryEventDetailContent = forwardRef<
  HTMLDivElement,
  {
    useVerticalView: boolean;
    setForceToVerticalView: (value?: boolean) => void;
    onRefInitialized: (node: HTMLDivElement | null) => void;
  }
>(
  (
    { useVerticalView, setForceToVerticalView, onRefInitialized },
    containerRef
  ) => {
    const { selectedTab, recenterMap } = useInventoryEventDetailContext();

    const isMobile = useMatchMedia('mobile');

    // Force vertical view for Purchase and sales
    useEffect(() => {
      const shouldUseVerticalView =
        selectedTab === SidePanelTab.PURCHASES ||
        selectedTab === SidePanelTab.SALES;
      setForceToVerticalView(shouldUseVerticalView);
    }, [selectedTab, setForceToVerticalView]);

    useEffect(() => {
      recenterMap();
    }, [recenterMap, useVerticalView]);

    return (
      <div
        ref={mergeRefs(containerRef, onRefInitialized)}
        className={clsx(styles.detailPanelRoot, {
          [styles.detailPanelRootMobile]: isMobile,
        })}
      >
        {isMobile && <SwiperButton dir="left" />}
        <Stack
          width="full"
          height="full"
          gap="m"
          className={clsx(styles.detailContainer, {
            [styles.detailContainerVertical]: useVerticalView,
          })}
        >
          <Stack
            direction="column"
            gap="m"
            height="full"
            width="full"
            className={clsx(styles.mapContainer, {
              [styles.mapContainerVertical]: useVerticalView,
              [styles.mapContainerMobile]: isMobile,
            })}
          >
            <MapHeader useVerticalView={useVerticalView} />
            <MapBody useVerticalView={useVerticalView} />
            {(selectedTab === SidePanelTab.LISTINGS ||
              selectedTab === SidePanelTab.NOTES) && (
              <MapFooter useVerticalView={useVerticalView} />
            )}
          </Stack>
          <Stack
            width="full"
            height="full"
            className={clsx(styles.tilesContainer, {
              [styles.tilesContainerVertical]: useVerticalView,
            })}
          >
            <DetailPanelBody useVerticalView={useVerticalView} />
          </Stack>
        </Stack>
      </div>
    );
  }
);

export const InventoryEventDetail = memo(InventoryEventDetailRaw);
